(() => {
  if (customElements.get('scout-purchase')) return;
  class ScoutPurchase extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.selector = this.querySelector('[data-scout-selector]');
      const firstDial = this.querySelector('[data-scout-dial]');
      this.dialChoice = firstDial ? firstDial.value : 'date';

      this.querySelectorAll('[data-scout-choice]').forEach(button => {
        button.addEventListener('click', () => {
          if (!this.selector) return;
          this.selector.value = button.dataset.scoutChoice;
          this.select();
          this.scrollToConfirm();
        }, { signal: this.controller.signal });
      });

      this.querySelectorAll('[data-scout-dial-button]').forEach(button => {
        button.addEventListener('click', () => {
          this.dialChoice = button.dataset.value || 'date';
          this.querySelectorAll('[data-scout-dial]').forEach(select => { select.value = this.dialChoice; });
          this.select();
        }, { signal: this.controller.signal });
      });

      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-scout-option]');
        if (panel && this.selector) { this.selector.value = panel.dataset.scoutOption; this.select(); }
      }, { signal: this.controller.signal });

      this.querySelectorAll('form.scout-order-form').forEach(form => form.addEventListener('submit', event => this.addToCart(event), { signal: this.controller.signal }));
      this.select();
      this.refresh();
      this.timer = window.setInterval(() => this.refresh(), 1000);
    }

    scrollToConfirm() {
      const selectedId = this.selector ? this.selector.value : '';
      const activePanel = Array.from(this.querySelectorAll('[data-scout-option]')).find(panel => panel.dataset.scoutOption === selectedId);
      const confirmStep = activePanel ? activePanel.querySelector('.scout-config-step__label--confirm') : null;
      if (!confirmStep) return;

      window.requestAnimationFrame(() => {
        const top = confirmStep.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }

    disconnectedCallback() { clearInterval(this.timer); if (this.controller) this.controller.abort(); }

    isLive() { return this.dataset.mode === 'live' || (this.dataset.mode === 'automatic' && Date.now() >= Date.parse(this.dataset.launch)); }

    refresh() {
      const live = this.isLive();
      if (live && this.dataset.commerceReady !== 'yes') this.loadCommerce();
      const pre = this.querySelector('[data-scout-prelaunch]');
      const liveBlock = this.querySelector('[data-scout-live]');
      if (pre) pre.hidden = live;
      if (liveBlock) liveBlock.hidden = !live;
      this.querySelectorAll('[data-scout-buy]').forEach(button => { if (!button.dataset.busy) button.disabled = !live; });
      this.syncOffers(live);
      document.querySelectorAll('[data-scout-release-link]').forEach(link => {
        const label = live ? (link.dataset.liveLabel || 'Choose your SCOUT') : (link.dataset.prelaunchLabel || 'Get release updates');
        if (link.textContent !== label) link.textContent = label;
      });
    }

    syncOffers(live) {
      this.querySelectorAll('[data-scout-offer]').forEach(template => {
        const next = template.nextElementSibling;
        let script = next && next.matches('[data-scout-offer-json]') ? next : null;
        if (!live) { if (script) script.remove(); return; }
        if (script) return;
        try { JSON.parse(template.innerHTML); } catch (e) { return; }
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.scoutOfferJson = '';
        script.textContent = template.innerHTML;
        template.after(script);
      });
    }

    select() {
      const selectedId = this.selector ? this.selector.value : '';
      this.querySelectorAll('[data-scout-choice]').forEach(button => {
        const active = button.dataset.scoutChoice === selectedId;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.querySelectorAll('[data-scout-option]').forEach(panel => {
        const active = panel.dataset.scoutOption === selectedId;
        panel.hidden = !active;
        const dial = panel.querySelector('[data-scout-dial]');
        if (dial) dial.value = this.dialChoice;

        panel.querySelectorAll('[data-scout-dial-button]').forEach(button => {
          const isDial = button.dataset.value === this.dialChoice;
          button.classList.toggle('is-active', isDial);
          button.setAttribute('aria-pressed', isDial ? 'true' : 'false');
        });

        panel.querySelectorAll('[data-scout-variant]').forEach(variant => { variant.hidden = variant.dataset.scoutVariant !== (dial ? this.dialChoice : 'date'); });

        const hero = panel.querySelector('[data-scout-selected-image]');
        if (hero) {
          const title = panel.querySelector('.scout-selected-copy h3')?.textContent?.trim() || '';
          if (title.includes('Digital + Compass')) {
            const dcSrc = 'https://cdn.shopify.com/s/files/1/1133/2956/files/scout-config-d-c.png?v=1789787666&selected=2';
            hero.src = dcSrc;
            hero.dataset.dateSrc = dcSrc;
            delete hero.dataset.noDateSrc;
          } else {
            const nextSrc = this.dialChoice === 'no-date' ? hero.dataset.noDateSrc : hero.dataset.dateSrc;
            if (nextSrc) hero.src = nextSrc;
          }
        }
      });

      this.querySelectorAll('[data-scout-card-image]').forEach(image => {
        if (!image.dataset.noDateSrc) return;
        const nextSrc = this.dialChoice === 'no-date' ? image.dataset.noDateSrc : image.dataset.dateSrc;
        if (nextSrc) image.src = nextSrc;
      });
    }

    async loadCommerce() {
      if (this.loadingCommerce || Date.now() < (this.nextCommerceCheck || 0)) return;
      this.loadingCommerce = true;
      this.nextCommerceCheck = Date.now() + 15000;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('scout_refresh', Date.now());
        const response = await fetch(url, { signal: this.controller.signal, cache: 'no-store' });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = doc.querySelector('scout-purchase');
        if (!replacement || replacement.dataset.commerceReady !== 'yes') return;
        replacement.dataset.mode = this.dataset.mode;
        const selector = replacement.querySelector('[data-scout-selector]');
        if (selector && this.selector) selector.value = this.selector.value;
        replacement.querySelectorAll('[data-scout-dial]').forEach(select => { select.value = this.dialChoice; });
        this.replaceWith(replacement);
      } catch (error) {
        if (error.name !== 'AbortError') this.nextCommerceCheck = Date.now() + 30000;
      } finally { this.loadingCommerce = false; }
    }

    async addToCart(event) {
      event.preventDefault();
      if (!this.isLive()) return;
      const form = event.target;
      const button = form.querySelector('[data-scout-buy]');
      if (!button || button.disabled) return;
      const status = form.querySelector('[data-scout-cart-status]');
      button.dataset.busy = 'true';
      button.disabled = true;
      if (status) status.textContent = 'Adding…';

      try {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const data = new FormData(form);
        if (cart && cart.getSectionsToRender) {
          data.append('sections', cart.getSectionsToRender().map(section => section.id));
          data.append('sections_url', window.location.pathname);
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }
        const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {method:'POST',headers:{'Accept':'application/json','X-Requested-With':'XMLHttpRequest'},body:data});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'This setup is no longer available. Please refresh the page.');
        if (cart && cart.renderContents) {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
          if (status) status.textContent = 'Added to cart.';
        } else {
          window.location.assign((window.routes && window.routes.cart_url) || '/cart');
        }
      } catch (error) {
        if (status) status.textContent = error.message || 'Unable to add this setup. Please try again.';
      } finally {
        delete button.dataset.busy;
        button.disabled = !this.isLive();
      }
    }
  }
  customElements.define('scout-purchase', ScoutPurchase);
})();
